import { useMemo, useRef } from 'react';

import {
  AccessibleContent,
  Button,
  Grid,
  GridItem,
  Strikeout,
  Text,
} from '@farmersdog/corgi-x';

import styles from './SubmitButton.css';

interface SubmitButtonProps {
  hasSelectedRecipes: boolean;
  onClick: () => void;
  formSubmitting: boolean;
  petDailySubtotal: string;
  petDailyDiscountedSubtotal: string;
  discountPercentage: number | null;
  isQuoteLoading: boolean;
  currentSelection: string[];
  trialDailyPriceLabel: string;
  hasQuote: boolean;
}

export function SubmitButton(props: SubmitButtonProps) {
  const {
    hasSelectedRecipes,
    onClick,
    formSubmitting,
    petDailySubtotal,
    petDailyDiscountedSubtotal,
    discountPercentage,
    isQuoteLoading,
    currentSelection,
    trialDailyPriceLabel,
    hasQuote,
  } = props;

  const previousSelectionLengthRef = useRef(0);

  const accesibleText = `Subtotal was ${petDailySubtotal} per day, it’s now ${petDailyDiscountedSubtotal} per day.`;

  // Determines if this is the first recipe selection by comparing the current and previous selection lengths.
  const isFirstSelectedRecipe = useMemo(() => {
    const isFirst =
      currentSelection.length === 1 && previousSelectionLengthRef.current === 0;
    previousSelectionLengthRef.current = currentSelection.length; // Update the ref
    return isFirst;
  }, [currentSelection]);

  const shouldShowSelectRecipesCopy = useMemo(
    () => !hasSelectedRecipes || (isFirstSelectedRecipe && isQuoteLoading),
    [hasSelectedRecipes, isFirstSelectedRecipe, isQuoteLoading]
  );

  return (
    <div className={styles.innerContainer}>
      <Grid>
        <GridItem className={styles.textContainer} justifyContent="center">
          {shouldShowSelectRecipesCopy && (
            <Text
              as="p"
              vSpacing="none"
              variant="article-16"
              color="charcoal-3"
              fontStyle="italic"
            >
              Select Recipes to Begin Building Plan
            </Text>
          )}
          {!shouldShowSelectRecipesCopy &&
            hasSelectedRecipes &&
            petDailyDiscountedSubtotal &&
            !isQuoteLoading && (
              <AccessibleContent
                accessibleText={accesibleText}
                visualContent={
                  <Text
                    variant="heading-16"
                    color="charcoal-3"
                    bold
                    as="p"
                    vSpacing="none"
                    className={styles.priceText}
                  >
                    {trialDailyPriceLabel}:{' '}
                    <>
                      <Strikeout className={styles.pricePerDay}>
                        <Text variant="heading-16">{petDailySubtotal}/day</Text>
                      </Strikeout>{' '}
                    </>
                    <Text variant="heading-16" as="span" color="kale-2" bold>
                      {petDailyDiscountedSubtotal}
                      /day{' '}
                    </Text>
                    <Text
                      variant="heading-16"
                      as="span"
                      color="kale-2"
                      bold
                      className={styles.pricePerDay}
                      id="recipes-select-plan-discount"
                    >
                      ({discountPercentage ?? ''}% OFF)
                    </Text>
                  </Text>
                }
              />
            )}
        </GridItem>
        <GridItem justifyContent="center">
          <Button
            className={styles.button}
            type="submit"
            aria-label={
              !hasSelectedRecipes
                ? 'Select Recipes to Begin Building Plan'
                : 'Continue'
            }
            aria-disabled={!hasSelectedRecipes || formSubmitting || !hasQuote}
            disabled={formSubmitting || !hasQuote}
            loading={formSubmitting}
            onClick={onClick}
          >
            Continue
          </Button>
        </GridItem>
      </Grid>
    </div>
  );
}
